"use client";

import React, { useState } from "react";

export type AlternativeTab = {
  id: string;
  label: string;
  content: React.ReactNode;
};

type Props = {
  baseId: string;
  tabs: AlternativeTab[];
};

/**
 * Detail tabs for one place. Every panel stays in the server HTML so the
 * full write-up is indexable; switching only toggles the `hidden` class.
 */
const AlternativeTabs: React.FC<Props> = ({ baseId, tabs }) => {
  const [active, setActive] = useState(tabs[0]?.id);

  return (
    <div className="flex flex-col gap-5">
      <div
        role="tablist"
        className="-mx-1 flex flex-row gap-1 overflow-x-auto px-1 pb-1"
      >
        {tabs.map((tab) => {
          const selected = tab.id === active;
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              id={`${baseId}-tab-${tab.id}`}
              aria-selected={selected}
              aria-controls={`${baseId}-panel-${tab.id}`}
              onClick={() => setActive(tab.id)}
              className={`flex-none whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm font-semibold transition-colors ${
                selected
                  ? "bg-primary text-primary-foreground"
                  : "bg-slate-100 text-zinc-700 hover:bg-slate-200"
              }`}
            >
              {tab.label}
            </button>
          );
        })}
      </div>
      {tabs.map((tab) => (
        <div
          key={tab.id}
          role="tabpanel"
          id={`${baseId}-panel-${tab.id}`}
          aria-labelledby={`${baseId}-tab-${tab.id}`}
          className={tab.id === active ? "flex flex-col gap-5" : "hidden"}
        >
          {tab.content}
        </div>
      ))}
    </div>
  );
};

export default AlternativeTabs;
